import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Header,
  SegmentedControl,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import EventsVersionSelector from "./EventsVersionSelector";
import { OrderIdContext, StageContext } from "../../contexes/OrderIdContext";
import { useMarketplaceId } from "../../contexes/MarketplaceIdContext";
import useFetchDetails from "../../hooks/useFetchDetails";
import {
  getCbeLogGroups,
  getEventDateRange,
  getEventLogGroups,
} from "../../helpers/logUtils";
import CbeVersionSelector from "../cbe/CbeVersionSelector";
import EventButtons from "../common/EventButtons";
import ErrorAlert from "../common/ErrorAlert";
import { useGlProduct } from "../../contexes/GlProductContext";
import { FlowType, useFlowType } from "../../hooks/useFlowType";

export default function Results() {
  const orderId = useContext(OrderIdContext);
  const stage = useContext(StageContext);
  const { marketplaceId } = useMarketplaceId();
  const { glProduct } = useGlProduct();

  const { data: detailsData } = useFetchDetails(stage, orderId);

  const eventData = detailsData?.events || [];
  const cbeData = detailsData?.cbes || [];

  const isEventNotEmpty = eventData.length > 0;
  const isCbeNotEmpty = cbeData.length > 0;

  const {
    flowType,
    setFlowType,
    showFlowTypeSelector,
    filteredEvents,
    filteredCbes,
  } = useFlowType({
    eventData,
    cbeData,
    glProduct,
  });

  const [selectedEventIndex, setSelectedEventIndex] = useState(() => {
    return filteredEvents.length > 0 ? filteredEvents.length - 1 : 0;
  });

  const [selectedCbeIndex, setSelectedCbeIndex] = useState(() => {
    return filteredCbes.length > 0 ? filteredCbes.length - 1 : 0;
  });

  useEffect(() => {
    if (
      filteredEvents.length > 0 &&
      selectedEventIndex >= filteredEvents.length
    ) {
      setSelectedEventIndex(filteredEvents.length - 1);
    }
  }, [filteredEvents, selectedEventIndex]);

  useEffect(() => {
    if (filteredCbes.length > 0 && selectedCbeIndex >= filteredCbes.length) {
      setSelectedCbeIndex(filteredCbes.length - 1);
    }
  }, [filteredCbes, selectedCbeIndex]);

  const eventLogGroups = useMemo(() => {
    return isEventNotEmpty ? getEventLogGroups(stage, eventData) : [];
  }, [isEventNotEmpty, stage, eventData]);

  const cbeLogGroups = useMemo(() => {
    return getCbeLogGroups(marketplaceId, stage);
  }, [marketplaceId, stage]);

  return (
    <Grid
      gridDefinition={[
        { colspan: { default: 12, s: 6 } },
        { colspan: { default: 12, s: 6 } },
      ]}
    >
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {isEventNotEmpty && (
                  <EventButtons
                    identifier={orderId}
                    stage={stage}
                    logGroups={eventLogGroups}
                    dateRange={getEventDateRange(
                      filteredEvents[selectedEventIndex] ||
                        eventData[selectedEventIndex],
                      false
                    )}
                    data={filteredEvents}
                  />
                )}
                {showFlowTypeSelector && (
                  <SegmentedControl
                    selectedId={flowType}
                    onChange={({ detail }) =>
                      setFlowType(detail.selectedId as FlowType)
                    }
                    options={[
                      { text: "Revenue", id: "accounting" },
                      { text: "Royalty", id: "document" },
                    ]}
                  />
                )}
              </SpaceBetween>
            }
          >
            Events
          </Header>
        }
      >
        {isEventNotEmpty && filteredEvents.length > 0 ? (
          <EventsVersionSelector
            eventData={filteredEvents}
            onVersionChange={setSelectedEventIndex}
          />
        ) : isEventNotEmpty ? (
          <ErrorAlert
            header={
              showFlowTypeSelector
                ? `No ${
                    flowType === "document" ? "royalty" : "revenue"
                  } events found`
                : "No events found"
            }
            content={
              showFlowTypeSelector
                ? `Try changing the flow type filter to view ${
                    flowType === "document" ? "revenue" : "royalty"
                  } events.`
                : ""
            }
          />
        ) : (
          <ErrorAlert header="Could not find any events" content="" />
        )}
      </Container>
      <Container
        header={
          <Header
            variant="h2"
            actions={
              isCbeNotEmpty && (
                <EventButtons
                  identifier={orderId}
                  stage={stage}
                  logGroups={cbeLogGroups}
                  dateRange={getEventDateRange(
                    filteredCbes[selectedCbeIndex] || cbeData[selectedCbeIndex],
                    true
                  )}
                  data={filteredCbes}
                />
              )
            }
          >
            CBEs
          </Header>
        }
      >
        {isCbeNotEmpty && filteredCbes.length > 0 ? (
          <CbeVersionSelector
            cbeData={filteredCbes}
            onVersionChange={setSelectedCbeIndex}
          />
        ) : isCbeNotEmpty ? (
          <ErrorAlert
            header={
              showFlowTypeSelector
                ? `No ${
                    flowType === "document" ? "royalty" : "revenue"
                  } CBEs found`
                : "No CBEs found"
            }
            content={
              showFlowTypeSelector
                ? `Try changing the flow type filter to view ${
                    flowType === "document" ? "revenue" : "royalty"
                  } CBEs.`
                : ""
            }
          />
        ) : (
          <ErrorAlert header="Could not find any CBEs" content="" />
        )}
      </Container>
    </Grid>
  );
}
